import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';

/**
 * Detects in-app browsers (WhatsApp, Instagram, Facebook, Messenger, LinkedIn,
 * Snapchat, Twitter, TikTok, Line). UPI intent URLs and Razorpay's UPI flow
 * cannot deep-link into native UPI apps from these sandboxed webviews, so we
 * tell the user to reopen in their real browser.
 */
const IN_APP_PATTERNS = [
  /WhatsApp/i,        // WhatsApp Web View
  /Instagram/i,
  /FBAN|FBAV|FB_IAB|FB4A/i,  // Facebook / Messenger
  /Messenger/i,
  /LinkedInApp/i,
  /Snapchat/i,
  /Twitter/i,
  /TikTok/i,
  /Line\//i,
];

const detectInApp = () => {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  for (const re of IN_APP_PATTERNS) {
    if (re.test(ua)) {
      if (/WhatsApp/i.test(ua)) return 'WhatsApp';
      if (/Instagram/i.test(ua)) return 'Instagram';
      if (/FBAN|FBAV|FB_IAB|FB4A|Messenger/i.test(ua)) return 'Facebook';
      if (/LinkedInApp/i.test(ua)) return 'LinkedIn';
      if (/Snapchat/i.test(ua)) return 'Snapchat';
      if (/Twitter/i.test(ua)) return 'Twitter';
      if (/TikTok/i.test(ua)) return 'TikTok';
      return 'In-app browser';
    }
  }
  return null;
};

const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const isIOS = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

const InAppBrowserWarning = ({ language = 'hi' }) => {
  const [appName, setAppName] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setAppName(detectInApp());
  }, []);

  if (!appName || dismissed) return null;

  const url = typeof window !== 'undefined' ? window.location.href : '';
  // Android: `intent://` opens directly in Chrome.
  const androidIntent = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
  const showAndroidBtn = isAndroid();
  const showIOSInstructions = isIOS();

  const t = language === 'hi'
    ? {
        title: 'Payment के लिए Browser में खोलें',
        body: `${appName} के अंदर UPI apps (PhonePe, GPay, Paytm) नहीं खुलते। कृपया इस link को Chrome / Safari में खोलें — सिर्फ़ 10 second का काम है।`,
        openChrome: 'Chrome में खोलें',
        iosInstr: 'iPhone पर: नीचे 3-dot ⋯ menu → "Open in Browser" / "Safari में खोलें"',
        copyUrl: 'Link Copy करें',
        copied: 'Link Copy हो गया!',
        dismiss: 'अभी छोड़ें',
      }
    : {
        title: 'Open in Browser to Pay',
        body: `UPI apps (PhonePe, GPay, Paytm) cannot be launched inside ${appName}. Please open this link in Chrome / Safari — takes 10 seconds.`,
        openChrome: 'Open in Chrome',
        iosInstr: 'iPhone: Tap the ⋯ menu below → "Open in Browser" / "Open in Safari"',
        copyUrl: 'Copy Link',
        copied: 'Link Copied!',
        dismiss: 'Dismiss',
      };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // older browsers
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (execErr) { console.warn('[copy] execCommand fallback failed:', execErr); }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div
      data-testid="inapp-browser-warning"
      className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border-2 border-saffron/40">
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 mt-1">
            <AlertTriangle className="w-7 h-7 text-saffron" />
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-bold text-warm-text ${language === 'hi' ? 'font-hindi' : ''}`}>
              {t.title}
            </h3>
          </div>
          <button
            data-testid="inapp-warning-close"
            onClick={() => setDismissed(true)}
            className="shrink-0 text-warm-text-muted hover:text-warm-text"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className={`text-sm text-warm-text-secondary leading-relaxed mb-5 ${language === 'hi' ? 'font-hindi' : ''}`}>
          {t.body}
        </p>

        <div className="flex flex-col gap-2.5">
          {showAndroidBtn && (
            <a
              data-testid="inapp-open-chrome"
              href={androidIntent}
              className="flex items-center justify-center gap-2 bg-saffron hover:bg-saffron/90 text-white font-bold py-3 px-4 rounded-xl transition"
            >
              <ExternalLink className="w-4 h-4" />
              <span className={language === 'hi' ? 'font-hindi' : ''}>{t.openChrome}</span>
            </a>
          )}
          {showIOSInstructions && (
            <div className="bg-saffron/5 border border-saffron/20 rounded-lg p-3 text-xs text-warm-text leading-relaxed">
              {t.iosInstr}
            </div>
          )}
          <button
            data-testid="inapp-copy-url"
            onClick={handleCopy}
            className="border-2 border-saffron text-saffron hover:bg-saffron/5 font-bold py-2.5 px-4 rounded-xl transition text-sm"
          >
            {copied ? t.copied : t.copyUrl}
          </button>
          <button
            data-testid="inapp-dismiss"
            onClick={() => setDismissed(true)}
            className="text-xs text-warm-text-muted hover:text-warm-text py-1"
          >
            {t.dismiss}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InAppBrowserWarning;
